import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
} from '@kouto/types';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import {
  getAnalyticsItemsFromProductsListing,
  getAnalyticsListNameFromProductLine,
} from 'features/analytics';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIsMobile } from 'WindowDimensionProvider';
import { Redirect, useParams } from 'react-router-dom';
import { ListingSeo } from 'components/organisms/ListingSeo';
import { useBrandId } from 'AppProvider';
import { EventLimitedTime } from 'features/Events/pages/EventDetailsPage/components/EventLimitedTime';
import PromotersCard from 'components/molecules/PromotersCard/PromotersCard';
import useBrandById from 'api/brand/useBrandById';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import isHyattEligible from 'utils/pointEligibility/isHyattEligible';
import NotFoundExperience from 'components/NotFoundExperience/NotFoundExperience';
import {
  EventAvailability,
  EventCoverMedia,
  EventDetails,
  EventDetailsHeader,
  EventGallery,
  EventHeadline,
  EventLocation,
} from './components';

// =======================================
// TEMPORARY EVENT REDIRECTS, keyed by brand id, then by the event id to redirect from
// TODO: Remove once these redirects are no longer needed
const EVENT_REDIRECTS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  // Silverado Resort
  '4f223dfa-6b4a-4568-9009-650fa1465df1': {
    'ef65ad01-b63c-4983-aa58-e03f49acdc04':
      '03c28e79-c056-416c-9c92-836e285e6bd3',
  },
  // The Singer Oceanfront Resort
  '94a231cf-c16e-4ab3-af64-bdf5b8c39122': {
    '01a08257-5f85-75ad-89e6-659bcba002f7':
      '01a11d2f-e3f9-7740-9e20-a15d90b8e118',
  },
};
// =======================================

const SCROLL_ANIMATION_FPS = 60;
const ANIMATION_PX_LENGTH = 600;

const getAnimationCompletion = (
  scrollFromTop: number,
  maxAnimationScroll: number,
) => {
  let animationCompletion = 0; // %
  if (scrollFromTop < 0) {
    animationCompletion = 100;
  } else if (scrollFromTop < maxAnimationScroll) {
    animationCompletion =
      Math.round(
        ((maxAnimationScroll - scrollFromTop) / maxAnimationScroll) * 10000,
      ) / 100;
  } else {
    animationCompletion = 0;
  }
  return animationCompletion;
};

interface EventDetailsPageProps {
  /* True when this page renders only as the backdrop beneath the mobile ticket-selection drawer.
     The ticket surface owns the page view for that URL, so firing VIEW_EXPERIENCE_DETAIL here too
     would send a second — and wrong — page-load beacon for the same page. */
  readonly suppressPageView?: boolean;
}

const EventDetailsPage = ({
  suppressPageView = false,
}: EventDetailsPageProps) => {
  const { eventId } = useParams<{ eventId: string }>();
  const brandId = useBrandId();

  const {
    isLoading,
    isLoadingListing,
    hasExchangeIdLoaded,
    exchangeIdError,
    listingError,
    listing,
  } = useEventBookingSession();

  const promoterBrand = useBrandById(listing?.providerBrandId ?? null);

  const redirectEventId = EVENT_REDIRECTS[brandId]?.[eventId];

  if (redirectEventId) {
    return <Redirect to={`/event/${redirectEventId}`} />;
  }

  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [animationCompletion, setAnimationCompletion] = useState(0);
  const disableAnimateScroll = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.DISABLE_ACTIVATE_SCROLLING_ANIMATION,
  );

  const onScroll = useCallback(
    throttle(() => {
      if (contentRef.current) {
        setAnimationCompletion(
          getAnimationCompletion(
            contentRef.current.getBoundingClientRect().top,
            isMobile ? ANIMATION_PX_LENGTH / 2 : ANIMATION_PX_LENGTH,
          ),
        );
      }
    }, Math.round(1000 / SCROLL_ANIMATION_FPS)),
    [contentRef, isMobile],
  );

  useEffect(() => {
    if (!disableAnimateScroll) {
      window.addEventListener('scroll', onScroll);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [disableAnimateScroll, onScroll]);

  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    listing?.id && listing?.brandId && listing?.productLine
      ? {
          listings: [
            {
              id: listing.id,
              brandId: listing.brandId,
              productLine: listing.productLine,
            },
          ],
        }
      : null,
  );

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_EXPERIENCE_DETAIL,
    payload: {
      [ANALYTICS_PROPERTY.ExperienceCategory]: listing?.category?.name,
      [ANALYTICS_PROPERTY.ExperienceId]: listing?.id,
      [ANALYTICS_PROPERTY.ExperienceProduct]: listing?.productLine,
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing?.title ?? 'Title not found',
      [ANALYTICS_PROPERTY.ListName]: getAnalyticsListNameFromProductLine(
        listing?.productLine,
      ),
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(
        getAnalyticsItemsFromProductsListing({
          listings: listing ? [listing] : [],
          product: listing?.productLine,
        }),
      ),
    },
    isNotReady: suppressPageView || isLoading || !listing,
  });

  // Show 404 if:
  // 1. Has real errors (exchangeIdError or listingError)
  // 2. Finished loading exchangeId AND listing BUT no listing found
  if (
    eventId &&
    hasExchangeIdLoaded &&
    !isLoadingListing &&
    (exchangeIdError || listingError || !listing)
  ) {
    return <NotFoundExperience />;
  }

  return (
    <EventDetailsPageWrapper>
      {/* Canonical (slug, falling back to the collection UUID) plus the page's SEO tags; rendered once
          the listing loads so a crawler following the sitemap's slug URL is never told otherwise. */}
      {listing && <ListingSeo listing={listing} />}
      <EventDetailsHeader promoterBrand={promoterBrand?.data} />
      <EventCoverMedia
        scrollAnimationDelay={animationCompletion}
        selectedMediaIndex={selectedMediaIndex}
        setSelectedMediaIndex={setSelectedMediaIndex}
        brandFlagName={
          eligibleProgram && Object.values(eligibleProgram)[0]?.payEligible
            ? Object.values(eligibleProgram)[0]?.org
            : null
        }
      />
      <GlassEffect
        ref={contentRef}
        active={!disableAnimateScroll}
        className="activate-glass-effect"
      >
        <EventDetailsMain>
          <EventInfo>
            <EventHeadline />
            <EventDetails hyattEligible={isHyattEligible(eligibleProgram)} />
            <EventLocation />
          </EventInfo>
          <SidebarColumnStack>
            {promoterBrand?.data?.name && (
              <PromotersCard promoterBrand={promoterBrand.data} withBorder />
            )}
            <EventLimitedTime />
            <EventAvailability />
          </SidebarColumnStack>
        </EventDetailsMain>
        <EventGallery />
      </GlassEffect>
    </EventDetailsPageWrapper>
  );
};

export const EventDetailsPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const GlassEffect = styled.div<{
  active: boolean;
}>`
  ${({ active }) =>
    active
      ? `
        width: 100vw;
        position: relative;
        left: 50%;
        transform: translate3d(-50vw, 0px, 0px);
        padding: 0px 12px;
        background: var(--way-colors-coherentColorShades-50);
        backdrop-filter: blur(60px);

        @media (min-width: 1200px) {
          padding: 0px max(24px, calc((100vw - 1280px) / 2));
        }
      `
      : ``};
`;

export const SidebarColumnStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const EventDetailsMain = styled.div`
  width: 100%;
  min-height: 400px;
  padding-top: 48px;
  display: flex;
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const EventInfo = styled.div`
  width: calc(100% - 350px);
  display: flex;
  flex-direction: column;
  padding-right: 96px;

  @media (max-width: 768px) {
    width: 100%;
    padding-right: 0;
  }
`;

export default EventDetailsPage;
